/**
 * Validation helpers for API requests
 */

import { ORDER_TYPE_MAP } from './precision.js';
import type { ApiOpen, ApiCloseAction, ApiCancelAction } from './api-schema.js';
import type { OpenOrderType } from './ostium-schema.js';
import type { ModifyInput } from './builder-types.js';

const DECIMAL_RE = /^-?\d+(\.\d+)?$/;

function assertDecimal(value: string, label: string): void {
  if (!DECIMAL_RE.test(value)) {
    throw new Error(`Invalid ${label}: "${value}" is not a valid decimal number`);
  }
  if (parseFloat(value) <= 0) {
    throw new Error(`Invalid ${label}: must be greater than zero, got "${value}"`);
  }
}

function assertNonNegativeDecimal(value: string, label: string): void {
  if (!DECIMAL_RE.test(value)) {
    throw new Error(`Invalid ${label}: "${value}" is not a valid decimal number`);
  }
  if (parseFloat(value) < 0) {
    throw new Error(`Invalid ${label}: must be non-negative, got "${value}"`);
  }
}

/**
 * Extract the contract OpenOrderType from an ApiOpen request.
 */
export function extractOpenType(open: ApiOpen): OpenOrderType {
  return ORDER_TYPE_MAP[open.t];
}

/**
 * Validate TP/SL direction relative to the open price.
 */
export function validateTpSlDirection(
  open: ApiOpen,
  tp: bigint,
  sl: bigint,
  openPrice: bigint,
): void {
  if (open.b) {
    if (tp > 0n && tp <= openPrice) {
      throw new Error(`Take profit (${open.tp}) must be above open price (${open.p}) for long positions`);
    }
    if (sl > 0n && sl >= openPrice) {
      throw new Error(`Stop loss (${open.sl}) must be below open price (${open.p}) for long positions`);
    }
  } else {
    if (tp > 0n && tp >= openPrice) {
      throw new Error(`Take profit (${open.tp}) must be below open price (${open.p}) for short positions`);
    }
    if (sl > 0n && sl <= openPrice) {
      throw new Error(`Stop loss (${open.sl}) must be above open price (${open.p}) for short positions`);
    }
  }
}

/**
 * Validate close parameters.
 */
export function validateCloseParams(close: ApiCloseAction): void {
  if (close.r < 1 || close.r > 100) {
    throw new Error(`Close percentage must be 1–100, got ${close.r}`);
  }
  assertDecimal(close.p, 'close price');
}

/**
 * Validate cancel parameters.
 */
export function validateCancelParams(cancel: ApiCancelAction): void {
  if (cancel.t === 'limit') {
    if (cancel.a === undefined || cancel.a < 0) {
      throw new Error(`Invalid pair index for limit cancel: ${cancel.a}`);
    }
    if (cancel.i === undefined || cancel.i < 0) {
      throw new Error(`Invalid order index for limit cancel: ${cancel.i}`);
    }
  } else {
    if (cancel.o === undefined || cancel.o < 0) {
      throw new Error(`Invalid order ID for ${cancel.t} cancel: ${cancel.o}`);
    }
  }
}

/**
 * Validate modify parameters.
 */
export function validateModifyParams(input: ModifyInput): void {
  if (input.a < 0) {
    throw new Error(`Invalid pair index: ${input.a}`);
  }
  if (input.i < 0) {
    throw new Error(`Invalid trade/order index: ${input.i}`);
  }
  if (!input.p && !input.tp && !input.sl) {
    throw new Error('At least one of price, takeProfit, or stopLoss must be provided');
  }
  if (input.p) {
    assertDecimal(input.p, 'price');
  }
  if (input.tp) {
    assertNonNegativeDecimal(input.tp, 'take profit');
  }
  if (input.sl) {
    assertNonNegativeDecimal(input.sl, 'stop loss');
  }
}
